import { type OrganizationMinimalListApiResponse, OrganizationType } from '@navio/nsdk';
import { URLKeys } from 'noddi-async';
import { type FilterOptionSource, usePagination } from 'noddi-ui-web';
import { useDebounceValue } from 'noddi-util';
import { toFilterOptionSource, useFilterOptionsGet } from '../useFilterOptionsGet';

type OrganizationOptionsArgs = {
  serviceDepartmentIds?: number[];
  serviceOrganizationIds?: number[];
  organizationTypes?: OrganizationType[];
  enabled?: boolean;
};

/** Title, description and visible options of this filter, for the builder next to its options. */
export const ORGANIZATION_FILTER_TEXT = {
  title: 'Organizations',
  description: 'Filter by specific organizations',
  defaultVisibleOptions: 3
};

/** Organizations as filter options, searchable. For `unit.multiSelect`. */
export const useOrganizationOptions = ({
  serviceDepartmentIds,
  serviceOrganizationIds,
  organizationTypes,
  enabled = true
}: OrganizationOptionsArgs = {}): FilterOptionSource<number> => {
  const { pageIndex, pageSize, search, setSearch } = usePagination();
  const debouncedSearch = useDebounceValue({ value: search, delay: 300 });

  const query = useFilterOptionsGet({
    type: URLKeys.getPaginatedMinimalOrganizations,
    input: {
      serviceDepartmentIds,
      serviceOrganizationIds,
      types: organizationTypes,
      pageSize,
      pageIndex,
      search: debouncedSearch
    },
    queryConfig: { enabled, select: toOrganizationOptions }
  });

  return toFilterOptionSource(query, 'number', { search, onSearch: setSearch });
};

const toOrganizationOptions = ({ results }: OrganizationMinimalListApiResponse) =>
  results.map((organization) => ({ value: organization.id, label: organization.name }));
